/**
 * MONSTER TOWN — THE HANDOVER. INTEGRATION GLUE (src/app/**).
 *
 * The moment the monster creator closes and the game becomes the kid's.
 *
 * ============ IT USED TO HAND OVER IN BUILD MODE. THAT WAS THE BUG. ============
 *
 * The round-2 fix this file was written for is still right: a fresh save must
 * not open on somebody else's finished town with nothing of the kid's on screen,
 * so the tick on the creator now frames their own plot and arms a free piece.
 *
 * What was wrong was the MODE. Handing over in build mode meant the first,
 * most natural, most discoverable action in the game — tap the ground, walk
 * there — dropped a crate instead. The owner opened his own game and could not
 * work out how to move. A three-year-old never recovers from that.
 *
 * So the handover is now PLAY:
 *
 *   * tapping the ground walks, which is what tapping the ground looks like it
 *     should do;
 *   * a free piece is still armed and the drawer is still one tap away, so
 *     nothing about building got further off — building is the thing you go
 *     LOOKING for, and looking for it is what the hammer is for;
 *   * the first-run tutorial (`src/ui/tutorial.ts`) takes it from here: a paw
 *     lands on the ground and waits, silently, for as long as it takes.
 *
 * RULES THIS OBEYS:
 *  - it happens ONCE per slot, on the slot's first monster, and never again;
 *  - nothing is forced and nothing is modal — every control stays live (LAW #2);
 *  - it is not an age mode (LAW #3): it is the same play mode every kid is in
 *    for the rest of the game;
 *  - not one word of it is text (LAW #4).
 */

import { bus } from '../core/events'
import { save } from '../core/save'
import type { PlaceableId } from '../core/types'
import { app, type GameContext } from './context'
import { ensureSelection, selectPlaceable } from '../build/state'
import { townCenter } from '../world/town'
import '../monster/events'
import '../ui/events'

/**
 * The piece a brand new kid gets handed. `block_wood` is in
 * `FREE_FOREVER_CATEGORIES`, so this can never fail on coins and never spends
 * any (economy: "basics are always free forever").
 *
 * It is armed but NOT armed-and-in-build-mode: the first tap walks. It is here
 * so that the moment a kid does find the hammer, there is already something
 * under their finger to put down.
 */
const FIRST_PIECE = 'block_wood' as PlaceableId

/** Close enough that the fenced plot fills the frame; wide enough to see it all. */
const PLOT_ZOOM = 1

export function installFirstRun(ctx: GameContext): void {
  bus.on('monster:created', () => {
    // `monster:created` fires once per slot, the first time the tick is tapped.
    // The creator closes itself first, so by the time this runs the town is back.
    //
    // PLAY, EXPLICITLY. `closeCreator()` restores whatever mode the kid was in
    // when the sheet opened, and on a first launch that is whatever boot left
    // behind — so this is stated rather than assumed.
    app.setMode('play')
    selectPlaceable(FIRST_PIECE)
    ensureSelection(FIRST_PIECE)

    // Frame the yard, not the town. ONE camera move, at the handover, and never
    // again — the tutorial that follows this deliberately never touches the
    // camera. The player system re-aims at the monster every frame and the
    // monster is standing on the plot, so all this has to do is come in from the
    // establishing wide shot; a pinch or a rotate still wins immediately.
    ctx.camera.setZoomIndex(PLOT_ZOOM, false)
    ctx.camera.lookAtPoint(townCenter(), false)

    bus.emit('audio:cue', { cue: 'sparkle_soft' })

    save.request('firstRun')
  })
}
